import {urls} from "src/constants/urls";
import AwsRumDomain from "src/serviceClients/aws-rum/awsRum-domain";
import AwsRumRealm from "src/serviceClients/aws-rum/awsRum-realm";

/**
 * This method builds the FCMenu regionalized URL from tool region, parsed from tool URL,
 * if tool URL is in valid stage, domain and region.
 *
 * @param toolUrl tool URL
 * @returns {string} FCMenu regionalized URL
 */
export function buildRegionalizedUrlFromToolUrl(toolUrl: string): string {
    if (!toolUrl) {
        // return default FCMenu internal URL if toolUrl is null
        return urls.FCMENU_INT_PROD_URL;
    }

    const internalRegex = /(https:\/\/)([a-z]{3}\.)((beta|gamma|prod)\.)(item-visibility\.returns\.amazon\.dev)/
    const internalStageRegex = /(?<=(https:\/\/)([a-z]{3}\.))((beta|gamma|prod)(?=(\.)(item-visibility\.returns\.amazon\.dev)))/
    const regionRegex = /(?<=([A-Za-z0-9-]*\.))(([a-z]{2}\.|)(?=((beta|integ|gamma)\.|)((((aftx|aft)\.|)(amazon\.com))|((aftx|aft)\.)(amazonoperations\.app))))/;
    const externalStageRegex = /(?<=([A-Za-z0-9-]*\.)([a-z]{2}\.|))((beta|integ|gamma)(?=(\.)((((aftx|aft)\.|)(amazon\.com))|((aftx|aft)\.)(amazonoperations\.app))))/;
    const preProdRegex = /([A-Za-z0-9-]*\.)(([a-z]{2}\.|)((beta|integ|gamma)\.)((((aftx|aft)\.|)(amazon\.com))|((aftx|aft)\.)(amazonoperations\.app)))/;

    // validate that url is not internal
    const isExternal = toolUrl.match(internalRegex) === null;
    if (!isExternal) {
        // Return integ URL as per stage
        const stageMatch = toolUrl.match(internalStageRegex) || [""];
        if (stageMatch[0] == "beta") {
            return urls.FCMENU_INT_INTEG_URL;
        }
        return urls.FCMENU_INT_PROD_URL;
    }

    // validate that url stage is not beta, integ or gamma
    const isProd = toolUrl.match(preProdRegex) === null;
    if (!isProd) {
        // return beta URL if stage is beta
        const stageMatch = toolUrl.match(externalStageRegex) || [""];
        if (stageMatch[0] == "beta") {
            return urls.FCMENU_EXT_BETA_URL;
        }
    }

    // extract region from toolUrl
    const matchArr = toolUrl.match(regionRegex) || [""];
    const region = matchArr[0].replace(/\./g, "");

    // build regionalized external URL if region is valid
    if (['na', 'fe', 'eu'].includes(region)) {
        return `https://menu.${region}.aft.amazonoperations.app`;
    } else {
        // return default FCMenu internal URL if tool isn't in FCMenu externalized domain
        return urls.FCMENU_INT_PROD_URL;
    }
}

/**
 * Determines the deployment stage based on the current URL
 * @returns The AWS RUM domain corresponding to the current environment
 */
export const getStage = (): AwsRumDomain | null => {
    const origin = window.location.origin;
    if (origin.includes(".gamma.")) {
        return AwsRumDomain.Gamma;
    } else if (origin.includes(".prod.")) {
        return AwsRumDomain.Prod;
    }

    return null;
};

/**
 * Determines the AWS region based on the current URL
 * @returns The AWS RUM realm corresponding to the current region, or null for unknown regions
 */
export const getRegion = (): AwsRumRealm | null => {
    const origin = window.location.origin;
    
    if (origin.includes("dub.") || origin.includes(".eu.")) {
        return AwsRumRealm.EU_WEST_1;
    } else if (origin.includes("pdx.") || origin.includes(".fe.")) {
        return AwsRumRealm.US_WEST_2;
    } else if (origin.includes("iad.") || origin.includes(".na.")) {
        return AwsRumRealm.US_EAST_1;
    }
    
    return null;
};


/**
 * Determines if the current environment is an AFT/MoC site.
 * AFT sites use domains like *.aft.amazon.com, *.aftx.amazon.com, or *.aft.amazonoperations.app.
 * Corp/internal sites use domains like *.amazon.dev.
 *
 * @returns true if the device is running at an AFT/MoC site
 */
export function isAFTEnvironment(): boolean {
    const origin = window.location.origin;
    return /\.aftx?\.(amazon\.com|amazonoperations\.app)/.test(origin);
}

/**
 * Determines if a printer identifier is an IP address (IPv4 or IPv6).
 * At MoC/AFT sites, Hip printers are identified by their static IP on FC WiFi.
 * This is used as a secondary check alongside isAFTEnvironment() for defensive routing.
 *
 * @param printerIdentifier - The value scanned/entered by the associate
 * @returns true if the identifier matches an IPv4 or IPv6 address pattern
 */
export function isIPAddress(printerIdentifier: string): boolean {
    if (!printerIdentifier) return false;
    const trimmed = printerIdentifier.trim();
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
    const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^([0-9a-fA-F]{1,4}:){1,7}:$|^::([0-9a-fA-F]{1,4}:){0,6}[0-9a-fA-F]{1,4}$|^([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}$/;
    return ipv4Regex.test(trimmed) || ipv6Regex.test(trimmed);
}
